/**
 * Permissões efetivas — regra pura, compartilhada entre servidor e cliente.
 *
 *     efetiva(membro, chave) = override explícito, se existir
 *                            | preset do papel
 *
 * O override guarda booleano, não apenas concessão: o dono precisa poder tanto
 * LIBERAR quanto RETIRAR algo que o preset do papel dá (Architecture Review
 * §5.1).
 *
 * O cliente usa isto só para ergonomia — ocultar o que não faz sentido, marcar
 * com 🔒 o que pode ser liberado. **A autorização real é sempre do servidor**,
 * que roda exatamente esta função sobre os dados do banco.
 */

export type TripRole = 'owner' | 'admin' | 'collaborator' | 'participant';

export type PermissionKey =
  | 'manage_members'
  | 'manage_permissions'
  | 'edit_itinerary'
  | 'reorder_itinerary'
  | 'create_expenses'
  | 'edit_others_expenses'
  | 'manage_places'
  | 'manage_trip_settings'
  | 'manage_documents'
  | 'manage_family'
  | 'export_trip';

export const PERMISSION_KEYS: readonly PermissionKey[] = [
  'manage_members',
  'manage_permissions',
  'edit_itinerary',
  'reorder_itinerary',
  'create_expenses',
  'edit_others_expenses',
  'manage_places',
  'manage_trip_settings',
  'manage_documents',
  'manage_family',
  'export_trip',
];

/**
 * Chaves RESERVADAS — existem no modelo (lista fechada, presets, overrides
 * persistidos) mas nenhum domínio implementado as consulta hoje: Lugares
 * salvos, Documentos e Exportação nunca entraram no produto. Decisão do PO
 * (13/09/2026): manter no schema/RBAC, **não expor** na tela de permissões
 * enquanto não tiverem efeito. Quando um desses domínios entrar, a chave sai
 * desta lista — e volta a aparecer — sem migração.
 */
export const RESERVED_PERMISSION_KEYS: readonly PermissionKey[] = [
  'manage_places',
  'manage_documents',
  'export_trip',
];

/** As chaves que a interface mostra e deixa ligar/desligar. */
export const VISIBLE_PERMISSION_KEYS: readonly PermissionKey[] = PERMISSION_KEYS.filter(
  (k) => !RESERVED_PERMISSION_KEYS.includes(k),
);

/**
 * Presets por papel — matriz do Architecture Review §5.3, com a decisão do
 * usuário de seguir o PRD no caso do admin: admin remove participante porque
 * TEM `manage_members` no preset, não por uma regra paralela na UI.
 *
 * Duas ausências merecem nota:
 *
 * - `edit_others_expenses` só do dono. Nos demais papéis, quem edita a despesa
 *   é quem a criou — inclusive o admin.
 * - `export_trip` só do dono. Exportar leva embora o conteúdo inteiro da
 *   viagem; transferir e encerrar seguem o mesmo critério.
 */
export const ROLE_PRESETS: Readonly<Record<TripRole, readonly PermissionKey[]>> = {
  owner: PERMISSION_KEYS,
  admin: [
    'manage_members',
    'manage_permissions',
    'edit_itinerary',
    'reorder_itinerary',
    'create_expenses',
    'manage_places',
    'manage_trip_settings',
    'manage_documents',
    'manage_family',
  ],
  collaborator: [
    'edit_itinerary',
    'reorder_itinerary',
    'create_expenses',
    'manage_places',
    'manage_documents',
    'manage_family',
  ],
  // Participante cria (atividade, despesa, item, mídia, documento) mas não
  // REORGANIZA o roteiro — é a única linha da matriz que separa participante
  // de colaborador na V1.
  participant: [
    'edit_itinerary',
    'create_expenses',
    'manage_places',
    'manage_documents',
    'manage_family',
  ],
};

export interface PermissionOverride {
  permission: PermissionKey;
  granted: boolean;
}

export function effectivePermissions(
  role: TripRole,
  overrides: readonly PermissionOverride[] = [],
): ReadonlySet<PermissionKey> {
  const efetivas = new Set<PermissionKey>(ROLE_PRESETS[role]);

  for (const override of overrides) {
    if (override.granted) efetivas.add(override.permission);
    else efetivas.delete(override.permission);
  }

  return efetivas;
}

export function hasPermission(
  role: TripRole,
  permission: PermissionKey,
  overrides: readonly PermissionOverride[] = [],
): boolean {
  return effectivePermissions(role, overrides).has(permission);
}

/* ------------------------------------------------------------------ edição */

export const ROLE_LABELS: Readonly<Record<TripRole, string>> = {
  owner: 'Dono',
  admin: 'Administrador',
  collaborator: 'Colaborador',
  participant: 'Participante',
};

/**
 * Papéis que a tela de permissões pode atribuir.
 *
 * `owner` fica de fora de propósito: virar dono é transferência de
 * propriedade, com confirmação forte e efeito sobre quem controla a viagem.
 * Deixar isso num seletor de papel transformaria uma decisão grave num toque
 * acidental.
 */
export const ASSIGNABLE_ROLES: readonly TripRole[] = ['admin', 'collaborator', 'participant'];

export type PermissionEditRejection =
  | 'cannot_edit_self'
  | 'missing_manage_permissions'
  | 'cannot_edit_owner'
  | 'cannot_assign_owner'
  | 'beyond_own_permissions';

export const PERMISSION_EDIT_MESSAGES: Record<PermissionEditRejection, string> = {
  cannot_edit_self: 'Você não pode alterar as próprias permissões.',
  missing_manage_permissions:
    'Você não tem permissão para gerenciar permissões nesta viagem.',
  cannot_edit_owner:
    'As permissões de quem é dono da viagem não podem ser alteradas. Transfira a propriedade primeiro.',
  cannot_assign_owner:
    'Para tornar alguém dono, use a transferência de propriedade.',
  beyond_own_permissions:
    'Você só pode conceder ou retirar permissões que você mesmo tem.',
};

export interface PermissionEdit {
  role: TripRole;
  overrides: readonly PermissionOverride[];
}

export interface PermissionActor {
  memberId: string;
  role: TripRole;
  permissions: ReadonlySet<PermissionKey>;
}

export interface PermissionTarget {
  memberId: string;
  role: TripRole;
  overrides: readonly PermissionOverride[];
}

export type PermissionEditCheck =
  | { ok: true; changed: PermissionKey[] }
  | { ok: false; reason: PermissionEditRejection; changed?: PermissionKey[] };

/**
 * Quem pode abrir a edição de permissões.
 *
 * O dono sempre pode — o preset dele é o conjunto inteiro, e a viagem é dele.
 * Os demais precisam da chave, sem atalho por papel: um administrador que teve
 * `manage_permissions` retirada por override não gerencia permissões.
 */
export function canManagePermissions(actor: {
  role: TripRole;
  permissions: ReadonlySet<PermissionKey>;
}): boolean {
  return actor.role === 'owner' || actor.permissions.has('manage_permissions');
}

/** Chaves cujo valor efetivo muda entre o estado atual e o pretendido. */
export function changedPermissions(
  target: PermissionTarget,
  edit: PermissionEdit,
): PermissionKey[] {
  const antes = effectivePermissions(target.role, target.overrides);
  const depois = effectivePermissions(edit.role, edit.overrides);
  return PERMISSION_KEYS.filter((k) => antes.has(k) !== depois.has(k));
}

/**
 * A edição é permitida?
 *
 * As três primeiras regras são invariantes de produto; a quarta é a que evita
 * escalada de privilégio:
 *
 * 1. **Ninguém edita as próprias permissões.** Sem isso, quem tem
 *    `manage_permissions` se concede tudo — a permissão de gerenciar
 *    permissões viraria a permissão de ter todas.
 *
 * 2. **As permissões do dono não se alteram.** O papel é que define o poder;
 *    mexer nas chaves criaria um dono sem controle da própria viagem.
 *
 * 3. **Ninguém vira dono por aqui.** Isso é transferência de propriedade.
 *
 * 4. **Quem não é dono só mexe no que tem.** Um administrador sem
 *    `export_trip` não concede `export_trip` — nem para outra pessoa, nem
 *    indiretamente ao trocar o papel dela.
 *
 * Antes de todas elas vem a porta de entrada: **quem edita precisa ter
 * `manage_permissions`**. A rota do servidor já exige a chave, mas a regra vive
 * aqui para que a tela saiba mostrar "sem acesso" em vez de um formulário
 * inteiro que termina em 403 — e para que a recusa não dependa de a política da
 * rota estar certa.
 */
export function checkPermissionEdit(params: {
  actor: PermissionActor;
  target: PermissionTarget;
  edit: PermissionEdit;
}): PermissionEditCheck {
  const { actor, target, edit } = params;

  if (actor.memberId === target.memberId) {
    return { ok: false, reason: 'cannot_edit_self' };
  }
  if (!canManagePermissions(actor)) {
    return { ok: false, reason: 'missing_manage_permissions' };
  }
  if (target.role === 'owner') {
    return { ok: false, reason: 'cannot_edit_owner' };
  }
  if (edit.role === 'owner') {
    return { ok: false, reason: 'cannot_assign_owner' };
  }

  const changed = changedPermissions(target, edit);

  if (actor.role !== 'owner') {
    const foraDoAlcance = changed.filter((k) => !actor.permissions.has(k));
    if (foraDoAlcance.length > 0) {
      return { ok: false, reason: 'beyond_own_permissions', changed: foraDoAlcance };
    }
  }

  return { ok: true, changed };
}

/**
 * Overrides mínimos para chegar ao conjunto desejado a partir de um papel.
 *
 * A tela trabalha com "ligado/desligado por chave"; o banco guarda apenas o
 * que DIVERGE do preset. Guardar todas as chaves faria uma mudança futura no
 * preset do papel não alcançar quem já existe — que é o oposto do que um
 * preset serve.
 */
export function minimalOverrides(
  role: TripRole,
  desired: ReadonlySet<PermissionKey>,
): PermissionOverride[] {
  const preset = new Set<PermissionKey>(ROLE_PRESETS[role]);
  return PERMISSION_KEYS.filter((k) => preset.has(k) !== desired.has(k)).map((k) => ({
    permission: k,
    granted: desired.has(k),
  }));
}
